import { useCallback } from 'react';

import {
   PromiseVoidFunction,
   TranslatableText,
   useSingleInterpolatedTranslationKey,
   useTranslate,
} from '@dreryk/react-common';
import { ActionEventInterceptor, useLocationWithBackState, usePushBack } from '@dreryk/react-components';

import { visitPaths } from '~libs.desktop.shared.paths';

import { appointmentsTranslationKeys, visitTranslationKeys } from '../../../translations';
import { AppointmentFormController } from '../shared/form/AppointmentForm.types';

import { AppointmentCreateActionEventInterceptor } from './interceptors/AppointmentCreateActionEventInterceptor';

const transformFirstLetterToLowerCase = (string: string): string =>
   string.charAt(0).toLocaleLowerCase() + string.slice(1);

type AppointmentPatientBookActionRenderer = (renderProps: { label: TranslatableText }) => JSX.Element;

type AppointmentPatientBookActionProps = Readonly<
   Pick<AppointmentFormController, 'handleSubmit'> & {
      children: AppointmentPatientBookActionRenderer;
      onAppointmentTimeSlotOccupiedFault: VoidFunction;
   }
>;

export const AppointmentPatientBookAction = (props: AppointmentPatientBookActionProps): JSX.Element => {
   const pushBack = usePushBack();
   const location = useLocationWithBackState();
   const translate = useTranslate();
   const label = useSingleInterpolatedTranslationKey(
      appointmentsTranslationKeys.create.modal.confirm.registerAndGoBack,
      'label',
      transformFirstLetterToLowerCase(translate(location?.state.label ?? visitTranslationKeys.goBackToAppointmentsList))
   );

   const handleSuccess = useCallback(() => {
      pushBack(visitPaths.getAppointmentsListPath());
   }, [pushBack]);

   return (
      <AppointmentCreateActionEventInterceptor
         handleSubmit={props.handleSubmit}
         onAppointmentTimeSlotOccupiedFault={props.onAppointmentTimeSlotOccupiedFault}
      >
         <ActionEventInterceptor action={handleSuccess as PromiseVoidFunction}>
            {props.children({ label: label })}
         </ActionEventInterceptor>
      </AppointmentCreateActionEventInterceptor>
   );
};
